import * as Track from "@axon-enterprise/track";
import { mixpanelTrack } from '@axon-enterprise/track';

import { getAppConfig } from '../../utils/config';
import { logger } from '../../utils/logger';

// TODO: Remove these once the axon-ui track package is updated to include these properties
export const TEMP_MX_PROPERTIES = {
  ApplianceID: 'Appliance ID',
  ApplianceSerialNumber: 'Appliance Serial Number',
  ApplianceBaseType: 'Appliance Base Type',
  ApplianceTotalCameras: 'Appliance Total Cameras',
  ApplianceMake: 'Appliance Make',
  ApplianceStatus: 'Appliance Status',
  ApplianceLocationID: 'Appliance Location ID',
  ApplianceOrganizationID: 'Appliance Organization ID',
  ApplianceOrganizationName: 'Appliance Organization Name',
  PageURL: 'Page URL',
  Protocol: 'Protocol',
  AppVersion: 'App Version',
  ConnectionMethod: 'Connection Method',
  WizardStep: 'Wizard Step',
  InputType: 'Input Type',
  PromptId: 'Prompt ID',
  PromptTextLength: 'Prompt Text Length',
  ResponseType: 'Response Type',
  ResponseLatencyMs: 'Response Latency Ms',
  DeviceModel: 'Device Model',
  DeviceID: 'Device ID',
  PreSetupDetected: 'Pre-Setup Detected',
} as const;

// TODO: Remove these once the axon-ui track package is updated to include these events
export const TEMP_MX_EVENTS = {
  RedirectAttempted: 'Redirect Attempted',
  SessionCreated: 'Session Created',
  SessionCreationFailed: 'Session Creation Failed',
  ChatbotOpened: 'Chatbot - Opened',
  ChatbotPromptSubmitted: 'Chatbot - Prompt Submitted',
  ChatbotResponse: 'Chatbot - Response',
  SetupBypassDetected: 'Setup - Bypass Detected',
} as const;

export type TrackingProperties = Record<string, unknown>;

const svcLogger = logger.withComponent('MixpanelService');

function createSessionId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof (crypto as any).randomUUID === 'function') {
      return (crypto as any).randomUUID();
    }
  } catch {}
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

class MixpanelService {
  private sessionId: string | null = null;
  private initialized = false;

  init(): void {
    if (this.initialized) return;
    const cfg = getAppConfig();
    if (!cfg.enableAnalytics) return;

    try {
      const existing = typeof window !== 'undefined' ? window.sessionStorage.getItem('tracking_session_id') : null;
      this.sessionId = existing || createSessionId();
      if (typeof window !== 'undefined' && !existing) {
        window.sessionStorage.setItem('tracking_session_id', this.sessionId);
      }

      (mixpanelTrack as any).registerSafely({
        [Track.MxPropertyName.SessionID]: this.sessionId,
        [Track.MxPropertyName.URL]: typeof window !== 'undefined' ? window.location.href : undefined,
      });

      this.initialized = true;
      // Note: do not clear the session on beforeunload; we want the session
      // to persist for the entire lifetime of the tab, including reloads.
    } catch (e) {
      svcLogger.warn('Failed to initialize MixpanelService', e as Error);
    }
  }

  getSessionId(): string | null {
    return this.sessionId;
  }

  setMetadata(metadata: TrackingProperties): void {
    const cfg = getAppConfig();
    if (!cfg.enableAnalytics) return;
    try {
      (mixpanelTrack as any).registerSafely(metadata);
    } catch (e) {
      svcLogger.warn('Failed to set metadata', e as Error);
    }
  }

  track(eventName: string, properties?: TrackingProperties): void {
    const cfg = getAppConfig();
    if (!cfg.enableAnalytics) return;
    try {
      const base: TrackingProperties = {
        [Track.MxPropertyName.SessionID]: this.sessionId || undefined,
        [Track.MxPropertyName.URL]: typeof window !== 'undefined' ? window.location.href : undefined,
        [TEMP_MX_PROPERTIES.Protocol]: typeof window !== 'undefined' ? window.location.protocol.replace(':', '') : undefined,
      };
      (mixpanelTrack as any).trackSafely(eventName, { ...base, ...(properties || {}) });
    } catch (e) {
      svcLogger.warn('Failed to track event', e as Error);
    }
  }
}

export const mixpanelService = new MixpanelService();

export function trackEvent(action: string, properties?: TrackingProperties): void {
  mixpanelService.track(action, properties);
}


